import logger from "@airhelp/node-logger";
import config from "config/config";
import type { LanguageCode } from "config/languages";
import type useExtendedRouter from "hooks/useExtendedRouter";
import type { OptimizelyTransformedExperiment } from "services/optimizely/types";
import type { ExperimentConfig, OptimizelyEvent, OptimizelyPushEvent } from "stores/OptimizelyStore";

const getOptimizely = () => {
  window.optimizely = window.optimizely || [];

  return window.optimizely;
};

export const getExperimentStates = () => getOptimizely().get("state")?.getExperimentStates();

export const pushOptimizelyConversionEvent = (event: OptimizelyPushEvent) => {
  getOptimizely().push(event);
};

export const getOptimizelyVariations = (callback: (event: OptimizelyEvent) => void) => {
  getOptimizely().push({
    type: "addListener",
    filter: {
      type: "lifecycle",
      name: "campaignDecided",
    },
    handler: callback,
  });
};

export const pushConversionEvent = (experimentConfig: ExperimentConfig) => {
  const { customEvent, experimentName, experimentId } = experimentConfig;
  return () =>
    customEvent
      ? pushOptimizelyConversionEvent(customEvent)
      : logger.warn(`No custom event defined for experiment: ${experimentName} experimentId: ${experimentId}`);
};

const buildCurrentPageUrl = (extendedRouter: ReturnType<typeof useExtendedRouter>, language: LanguageCode) => {
  if (language === "en-ca") {
    return `${config.domainEnCa}${extendedRouter.asPath}`;
  }
  return `${config.websiteUrl}${extendedRouter.asPath}`;
};

export const trimTrailingSlash = (url: string) => {
  if (url.endsWith("/")) {
    return url.slice(0, -1);
  }
  return url;
};

const isValidURL = (string: string) => {
  // Regular expression to match a valid URL
  const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
  return urlPattern.test(string);
};

const simplifyURL = (url: string) => {
  if (!isValidURL(url)) {
    return false;
  }

  const parsedUrl = new URL(url);
  const urlString = `${parsedUrl.protocol}//${parsedUrl.host}${parsedUrl.pathname}`;

  return trimTrailingSlash(urlString);
};

export const resolveCondition = (condition, url: string) => {
  if (Array.isArray(condition)) {
    const operator = condition[0].toLowerCase();
    const operands = condition.slice(1);

    if (operator === "or") {
      return operands.some((operand) => resolveCondition(operand, url));
    }

    if (operator === "and") {
      return operands.every((operand) => resolveCondition(operand, url));
    }

    if (operator === "not") {
      return !resolveCondition(operands[0], url);
    }
  }

  if (typeof condition === "object" && condition.type === "url") {
    const { value } = condition;
    if (condition.match_type === "simple") {
      return simplifyURL(value) === simplifyURL(url);
    }
    if (condition.match_type === "exact") {
      return trimTrailingSlash(url) === trimTrailingSlash(value);
    }
    if (condition.match_type === "substring") {
      return url.includes(value);
    }
    if (condition.match_type === "regex") {
      const regex = new RegExp(value);
      return regex.test(url);
    }
  }

  return false;
};

export const shouldInsertOptimizelySnippet = (
  experiments: OptimizelyTransformedExperiment[],
  extendedRouter: ReturnType<typeof useExtendedRouter>,
  language: LanguageCode,
  forceDraftExperiments = false
) => {
  const shouldIncludeDrafts =
    config.buildEnv === "development" || config.buildEnv === "staging" || forceDraftExperiments;
  const runningExperiments = experiments.filter(({ status }) => status === "running");
  const experimentList = shouldIncludeDrafts ? experiments : runningExperiments;
  const experimentsConditions = experimentList.map(({ url_targeting: { conditions } }) => [...conditions]);
  const currentURL = buildCurrentPageUrl(extendedRouter, language);
  const resolvedConditions = experimentsConditions.map((condition) => resolveCondition(condition, currentURL));

  if (resolvedConditions.includes(true)) {
    return true;
  }
  return false;
};

export const setIncludeDraftExperiments = () => {
  if (window?.location.search.includes("include_draft_experiments=true")) {
    sessionStorage.setItem("include_draft_experiments", "true");
  }
};

export const getIncludeDraftExperiments = () => sessionStorage.getItem("include_draft_experiments") === "true";
